import React, { useEffect } from 'react';
import { useAuth } from '../store/hooks/useAuth';
import { useService } from '../store/slice/serviceSlice';
import { useBooking } from '../store/slice/bookingSlice';
import { clientLabel, serviceLabel, dateLabel, timeLabel, statusLabel } from '../utils/bookingDisplay';
import { Link } from 'react-router-dom';

function DashboardPage() {
  const { user, isAuthenticated } = useAuth();
  const { services, fetchServices } = useService();
  const { bookings, fetchBookings } = useBooking();

  useEffect(() => {
    fetchServices();
    fetchBookings();
  }, []);
  

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-slate-500">Please log in to view the dashboard</p>
      </div>
    );
  }

  const upcomingFiltered = bookings.filter((b) => {
    const dStr = dateLabel(b);
    if (!dStr) return false;
    const st = statusLabel(b);
    if (st === 'cancelled' || st === 'completed') return false;
    return new Date(dStr) >= new Date(new Date().setHours(0, 0, 0, 0));
  });
  const upcomingBookings = upcomingFiltered.slice(0, 5);
  const totalRevenue = services?.reduce((sum, service) => sum + (service.base_price ?? service.price ?? 0), 0);

  const statCards = [
    {
      title: 'Total Services',
      value: services.length,
      icon: '✂️',
      color: 'from-violet-500 to-purple-600',
      bgLight: 'bg-violet-50',
      link: '/services',
    },
    {
      title: 'Total Bookings',
      value: bookings.length,
      icon: '📅',
      color: 'from-blue-500 to-indigo-600',
      bgLight: 'bg-blue-50',
      link: '/bookings',
    },
    {
      title: 'Revenue (Services)',
      value: `$${totalRevenue}`,
      icon: '💰',
      color: 'from-emerald-500 to-teal-600',
      bgLight: 'bg-emerald-50',
    },
    {
      title: 'Upcoming Today',
      value: upcomingFiltered.length,
      icon: '🕐',
      color: 'from-amber-500 to-orange-600',
      bgLight: 'bg-amber-50',
      link: '/bookings',
    },
  ];

  const StatIcon = ({ icon }) => (
    <div className="absolute top-5 right-5 w-12 h-12 rounded-xl bg-white/90 shadow-premium flex items-center justify-center text-xl">
      {icon}
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Welcome header */}
      <div className="relative overflow-hidden rounded-premium-xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 text-white shadow-premium-xl">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.04\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-80" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
              Welcome back, {user?.name || 'Admin'}
            </h1>
            <p className="mt-2 text-slate-400 text-base">Here&apos;s what&apos;s happening with your salon today.</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/bookings"
              className="px-5 py-2.5 rounded-premium bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/20 font-medium text-sm transition-all"
            >
              View Bookings
            </Link>
            <Link
              to="/services"
              className="px-5 py-2.5 rounded-premium bg-amber-500 hover:bg-amber-600 text-white font-medium text-sm shadow-accent transition-all"
            >
              Manage Services
            </Link>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {statCards.map((stat) => (
          <Link
            key={stat.title}
            to={stat.link || '#'}
            className={`group relative overflow-hidden rounded-premium-lg p-6 card-premium hover:shadow-premium-lg transition-all duration-250 ${!stat.link ? 'cursor-default' : 'hover:border-amber-200/60'}`}
          >
            <StatIcon icon={stat.icon} />
            <p className="text-sm font-medium text-slate-500">{stat.title}</p>
            <p className="mt-2 text-2xl font-bold text-slate-800 tracking-tight">{stat.value}</p>
            {stat.link && (
              <p className="mt-2 text-sm text-amber-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                View →
              </p>
            )}
          </Link>
        ))}
      </div>

      {/* Content grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Services */}
        <div className="card-premium overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-display font-semibold text-lg text-slate-800">Recent Services</h2>
            <Link to="/services" className="text-amber-600 hover:text-amber-700 font-medium text-sm transition-colors">
              View all
            </Link>
          </div>
          <div className="p-6">
            {services.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.242 4.242 3 3 0 004.242-4.242zm0-5.758a3 3 0 10-4.242-4.242 3 3 0 004.242 4.242z" />
                </svg>
                <p>No services yet. Add one to get started!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {services.slice(0, 5).map((service) => (
                  <div
                    key={service.id}
                    className="flex items-center justify-between p-4 rounded-premium bg-slate-50/80 hover:bg-amber-50/40 transition-colors"
                  >
                    <div>
                      <h4 className="font-medium text-slate-800">{service.name}</h4>
                      <p className="text-sm text-slate-500">
                        ${parseFloat(service.base_price ?? service.price ?? 0).toFixed(2)} • {service.duration_minutes ?? service.duration ?? 0} mins
                      </p>
                    </div>
                    <span className="px-3 py-1 rounded-lg bg-amber-100 text-amber-700 text-sm font-medium">
                      ${parseFloat(service.base_price ?? service.price ?? 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming bookings */}
        <div className="card-premium overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-display font-semibold text-lg text-slate-800">Upcoming Bookings</h2>
            <Link to="/bookings" className="text-amber-600 hover:text-amber-700 font-medium text-sm transition-colors">
              View all
            </Link>
          </div>
          <div className="p-6">
            {bookings.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p>No bookings scheduled</p>
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="flex items-center justify-between p-4 rounded-premium bg-slate-50/80 hover:bg-blue-50/40 transition-colors border-l-4 border-amber-400"
                  >
                    <div>
                      <h4 className="font-medium text-slate-800">{clientLabel(booking)}</h4>
                      <p className="text-sm text-slate-500">
                        {dateLabel(booking) || '—'} {timeLabel(booking) ? `at ${timeLabel(booking)}` : ''}
                      </p>
                      <p className="text-xs text-amber-600 font-medium mt-0.5">{serviceLabel(booking)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
